import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { animationTransition, animationTransitionExt } from '@utils/animationTransition';
import { styled } from '@styledComponents';
import { Text } from '@components/typography';
import { JoykitColor, LinkVariant, Theme } from '@components/themes';
import { Button } from '@components/Button';
import { CTALink } from '@components/CTALink';
import { CTAGatsbyLink } from '@components/CTAGatsbyLink';
import { Columns } from '@components/Columns';
import { variant } from 'styled-system';
import { Flex } from '@components/Flex';
import { JoykitIcon } from '@components/JoykitIcon';
import {
  JOY_LOGO_WIDTH_MOBILE,
  JOY_LOGO_WIDTH_DESKTOP,
  JOY_LOGO_WIDTH_XLARGE,
  TOP_BAR_JOY_ICON_DESKTOP_RIGHT,
  TOP_BAR_JOY_ICON_TABLET_RIGHT,
} from './ParticleJoyLogo.styles';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { StyledBannerContainer, TOP_BANNER_HEIGHT } from './AnnounceBanner.styles';
import { CloudImage } from '@components/CloudImage';

export const TOP_BAR_DEFAULT_HEIGHT_DESKTOP = '162px';
export const TOP_BAR_COLLAPSED_HEIGHT_DESKTOP = '80px';
export const TOP_BAR_DEFAULT_HEIGHT_MOBILE = '58px';
export const TOP_BAR_NAV_PADDING_TABLET = 32;
export const TOP_BAR_NAV_PADDING_DESKTOP = 48;

export const NavMenuLgHeaderExternalLink = styled(CTALink)``;

export const NavMenuLgHeaderLink = styled(CTAGatsbyLink)``;

export const StyledNavMenuLgWrapper = styled(Flex)<{ left?: number }>`
  width: 100%;
  ${NavMenuLgHeaderLink}, ${NavMenuLgHeaderExternalLink} {
    display: inline-block;
    min-width: 325px;
    width: 100%;

    span {
      color: inherit;
      font-style: inherit;
    }

    padding-left: ${props => (props.left || 0) + JOY_LOGO_WIDTH_MOBILE}px;
    ${themeMediaQuery('sm')} {
      padding-left: ${props =>
        (props.left || 0) + JOY_LOGO_WIDTH_MOBILE + TOP_BAR_NAV_PADDING_TABLET / 2 + TOP_BAR_JOY_ICON_TABLET_RIGHT}px;
    }
    ${themeMediaQuery('md')} {
      padding-left: ${props =>
        (props.left || 0) + JOY_LOGO_WIDTH_DESKTOP + TOP_BAR_NAV_PADDING_DESKTOP / 2 + TOP_BAR_JOY_ICON_DESKTOP_RIGHT}px;
    }
    ${themeMediaQuery('xl')} {
      padding-left: ${props =>
        (props.left || 0) + JOY_LOGO_WIDTH_XLARGE + TOP_BAR_NAV_PADDING_DESKTOP / 2 + TOP_BAR_JOY_ICON_DESKTOP_RIGHT}px;
    }
  }
`;

export const ToggleNavParent = styled(Text)<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding-top: ${themeSpace(1)};
  padding-bottom: ${themeSpace(1)};
  padding-left: ${TOP_BAR_NAV_PADDING_TABLET / 2}px;
  padding-right: ${TOP_BAR_NAV_PADDING_TABLET / 2}px;
  ${themeMediaQuery('md')} {
    padding-left: ${TOP_BAR_NAV_PADDING_DESKTOP / 2}px;
    padding-right: ${TOP_BAR_NAV_PADDING_DESKTOP / 2}px;
  }
  > span {
    white-space: normal;
    color: ${props => (props.isActive ? themeColor('yellow5') : 'inherit')};
  }
  svg {
    margin-left: 10px;
    flex-shrink: 0;
    transform: ${({ isActive }) => (isActive ? 'rotate(-180deg)' : 'none')};
    color: ${props => (props.isActive ? themeColor('yellow5') : 'inherit')};
  }
`;

export const StyledGetStartedButton = styled(GetStartedButton)`
  border-radius: 4px;
  padding: 0;
  width: 120px;
  height: 43px;
  border-width: 2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  line-height: 47px;
  /* Mobile breakpoint */
  ${themeMediaQuery('sm')} {
    width: 144px;
  }
`;

export const topHeaderLinkHeroTransitionClassName = 'top-header-link-hero-transition';
export const topBarLinksFindCouple = 'top-header-find-couple';

export const NavMenuDesktopTopBar = styled.nav<{ hiddenMenu: boolean; isScrollCollapsed: boolean }>`
  width: 100%;
  transform: translate(0, ${({ hiddenMenu }) => (hiddenMenu ? '-100%' : '0')});
  display: flex;
  flex-direction: row;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: ${animationTransition('transform')}, ${animationTransitionExt({ property: 'background-color', duration: '250ms' })};
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const NavMenuDesktopDropShell = styled.div`
  position: relative;
  left: calc(50%);
  transform: translateX(calc(-50%));
  width: calc(100%);
  transition: ${animationTransition('bottom')};
`;

export const NavMenuDesktopDropShellWrapper = styled.div<{ isOpen: boolean; isOpening: boolean; isScrollCollapsed: boolean }>`
  position: absolute;
  top: 0;
  width: 100%;
  padding-top: ${({ isScrollCollapsed }) =>
    isScrollCollapsed ? TOP_BAR_COLLAPSED_HEIGHT_DESKTOP : /*TOP_BAR_DEFAULT_HEIGHT_DESKTOP*/ 162 + TOP_BANNER_HEIGHT + 'px'};
  padding-bottom: 65px;
  transform: ${({ isOpen, isOpening }) => (isOpen && isOpening ? 'translateY(0)' : 'translateY(-100%)')};
  transition: ${animationTransitionExt({ property: 'transform', duration: '500ms', timingFunction: 'ease-in-out' })};
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const NavMenuMobileTopBar = styled.nav<{ isOpen: boolean }>`
  height: ${({ isOpen }) => (isOpen ? 76 : 88)}px;
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 2;
  transition: ${animationTransition('height')}, ${animationTransitionExt({ property: 'background-color', duration: '250ms' })};
`;

export const NavMenuMobileHamburgerImgWrapper = styled.span<{ variant?: LinkVariant }>`
  display: inline-block;
  transition: ${animationTransition('color')};
  svg {
    margin-bottom: -3px;
  }
  ${variant({
    scale: 'links',
    variants: {
      primary: {},
    },
  })}
  color: #333333;
`;

export const NavMenuMobileDropShell = styled.div<{ isOpen: boolean; isScrollCollapsed: boolean }>`
  padding-top: ${({ isOpen, isScrollCollapsed }) => (isOpen ? 76 : 58) + (isScrollCollapsed ? 0 : TOP_BANNER_HEIGHT)}px;
  position: absolute;
  top: 0;
  width: 100%;
  overflow: ${({ isOpen }) => (isOpen ? 'auto' : 'hidden')};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  height: 100vh;
  transition: ${animationTransition('transform', 'max-height')};
`;

interface HeaderProps {
  theme: Theme;
  isTransparent: boolean;
  isMenuOpen: boolean;
  backgroundColor: JoykitColor;
}

const headerBackground = ({ theme, isTransparent, isMenuOpen, backgroundColor }: HeaderProps): string =>
  isTransparent ? 'rgba(0, 0, 0, 0)' : isMenuOpen ? theme.colors['yellow1'] : theme.colors[backgroundColor || 'mono7'];

const headerMobileBackground = (): string => 'transparent';

export const StyledHeader = styled.header<{
  isTransparent: boolean;
  backgroundColor: JoykitColor;
  isMenuOpen: boolean;
  hiddenMenu: boolean;
  isScrollCollapsed: boolean;
}>`
  position: ${({ isScrollCollapsed }) => (isScrollCollapsed ? 'fixed' : 'absolute')};
  visibility: ${({ hiddenMenu }) => (hiddenMenu ? 'hidden' : 'visible')};
  z-index: 100;
  top: 0;
  min-width: 100%;
  transition: ${animationTransition('all')};
  height: ${({ isMenuOpen }) => (isMenuOpen ? '100%' : TOP_BAR_DEFAULT_HEIGHT_MOBILE)};

  ${NavMenuDesktopTopBar} {
    height: ${({ isScrollCollapsed }) => (isScrollCollapsed ? TOP_BAR_COLLAPSED_HEIGHT_DESKTOP : TOP_BAR_DEFAULT_HEIGHT_DESKTOP)};
    background-color: ${headerBackground};
    backdrop-filter: ${props => (props.isTransparent || props.isMenuOpen ? 'none' : 'saturate(180%) blur(20px)')};

    .${topHeaderLinkHeroTransitionClassName} {
      color: ${({ isTransparent, theme }) => (isTransparent ? 'inherit' : theme.colors['neutra6'])};
      &:hover {
        color: ${({ isTransparent }) => (isTransparent ? 'inherit' : themeColor('yellow5'))};
      }
      &:active {
        color: ${({ isTransparent }) => (isTransparent ? 'inherit' : themeColor('yellow3'))};
      }
    }
  }

  ${NavMenuMobileTopBar} {
    background-color: ${headerMobileBackground};
  }
  ${NavMenuDesktopDropShellWrapper} {
    background-color: ${({ theme }) => theme.colors['yellow1']};
    box-shadow: ${({ isMenuOpen }) =>
      !isMenuOpen ? 'none' : '0px 50px 100px -20px rgba(93, 81, 50, 0.05), 0px 30px 60px -30px rgba(0, 0, 0, 0.06)'};
  }
  ${NavMenuMobileDropShell} {
    background-color: ${({ theme }) => theme.colors['yellow1']};
  }
  ${StyledBannerContainer} {
    background: ${({ isMenuOpen }) => (isMenuOpen ? themeColor('purple5') : themeColor('announcementPurple'))};
    ${themeMediaQuery('below_sm')} {
      background: ${themeColor('purple5')};
    }
  }

  .nav-menu-left {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .nav-menu-right a:first-child {
    margin-left: 0;
  }

  .nav-menu-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  ${StyledGetStartedButton} {
    margin-left: ${TOP_BAR_NAV_PADDING_TABLET / 2}px;
    ${themeMediaQuery('md')} {
      margin-left: ${TOP_BAR_NAV_PADDING_DESKTOP / 2}px;
    }
    ${themeMediaQuery('below_sm')} {
      margin-left: 0px;
    }
  }

  ${themeMediaQuery('sm')} {
    height: ${({ isScrollCollapsed }) => (isScrollCollapsed ? TOP_BAR_COLLAPSED_HEIGHT_DESKTOP : TOP_BAR_DEFAULT_HEIGHT_DESKTOP)};
    .nav-menu-left,
    .nav-menu-right {
      height: 58px;
    }
  }

  .${topBarLinksFindCouple} {
    display: none;

    ${themeMediaQuery('md')} {
      display: block;
    }
  }
`;

export const AnnounceBannerWrapper = styled.div<{
  isScrollCollapsed: boolean;
}>`
  position: ${({ isScrollCollapsed }) => (isScrollCollapsed ? 'fixed' : 'relative')};
  width: 100%;
  visibility: ${({ isScrollCollapsed }) => (isScrollCollapsed ? 'hidden' : 'visible')};
  z-index: 1;
`;

export const DesktopColumns = styled(Columns)`
  height: 100%;
`;

export const NavMenuWrapperMobile = styled.div<{ isScrollCollapsed: boolean }>`
  height: ${({ isScrollCollapsed }) => (isScrollCollapsed ? '100%' : `calc(100% - ${TOP_BANNER_HEIGHT}px)`)};
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const MobileNavHeaderExternalLink = styled(CTALink)`
  width: 100%;
  span {
    color: inherit;
    font-style: inherit;
  }
`;

export const MobileNavHeaderLink = styled(CTAGatsbyLink)`
  width: 100%;
  span {
    color: inherit;
    font-style: inherit;
  }
`;

export const MobileGetStartedButton = styled(GetStartedButton)`
  border-radius: 4px;
  padding: 0 24px;
  width: 100%;
`;

export const MobileLoginButton = styled(Button)`
  width: 100%;
  margin-top: 16px;
`;

export const MobileFindButton = styled(CTALink)`
  margin-top: 24px;
  justify-content: center;
  width: 100%;
  height: 56px;
`;

export const HeaderSpacingStub = styled.div`
  height: ${TOP_BAR_DEFAULT_HEIGHT_MOBILE};
  ${themeMediaQuery('sm')} {
    height: ${TOP_BAR_DEFAULT_HEIGHT_DESKTOP};
  }
`;

export const FindCoupleWrapper = styled(Flex)`
  padding-top: 20px;
  padding-bottom: 20px;
  width: 100%;
  ${themeMediaQuery('below_sm')} {
    padding: 40px 16px 20px;
  }
`;

export const StyledCloseButton = styled(Button)`
  float: right;
  top: 24px;
  right: 24px;
  padding: 4px;
  background: none;
`;

export const StyledCloseIcon = styled(JoykitIcon)`
  width: 16px;
  height: 16px;
`;

export const MobileNavSecondPanel = styled.div`
  background-color: ${themeColor('mono7')};
  padding: 16px 16px 16px;
  margin-top: 28px;
`;

export const HamburgerButton = styled(Button)<{ isClosed: string | null }>`
  padding: 8px;
  outline: unset !important;
  color: unset;
  line-height: 24px;
  height: 40px;
  width: ${({ isClosed }) => (isClosed ? 'auto' : '50px')};
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(32px);
  border-radius: 64px;
  border: none;
  &:hover,
  &:active {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

export const MobHeaderContent = styled(Flex)`
  justify-content: space-between;
  width: 100%;
  a {
    margin-left: -19%;
  }
`;

export const MobGetStartedButton = styled(GetStartedButton)`
  border-radius: 100em 100em 100em 100em;
  margin-right: ${themeSpace(5)};
  line-height: normal;
  height: 40px;
  width: 123px;
  padding: ${themeSpace(0)};
  padding-top: calc(${themeSpace(3)} + 1px);
  padding-bottom: ${themeSpace(3)};
  border: none;
  box-shadow: 0px 2px 5px -1px rgba(68, 50, 93, 0.25), 0px 1px 3px -1px rgba(0, 0, 0, 0.3);
`;

export const StaticLogo = styled(CloudImage)`
  width: 64px;
  height: 40px;
  margin-right: ${themeSpace(6)};
`;
